import React, { useCallback, useRef, useState } from "react";

import { faExpand } from "@fortawesome/pro-regular-svg-icons";
import { twJoin } from "tailwind-merge";

import { FloatingWindow } from "src/base-components/FloatingWindow/FloatingWindow";
import { useStringCellHeight } from "src/cases/caseView/blocks/cellEditors/useStringCellHeight";
import { MIN_STRING_CELL_HEIGHT } from "src/cases/caseView/blocks/cellTypes";
import { IconButton } from "src/design-system/Icon";
import { Tooltip } from "src/design-system/Tooltip";
import { useOnClickOutside } from "src/hooks/usehooks-ts";
import { copyTextToClipboard } from "src/utils/clipboard";

export const StringTextareaEditor: React.FC<{
  value: unknown;
  onChange: (newValue: unknown) => void;
  onBlur: () => void;
  hasLeftIcon?: boolean;
  columnKey: string;
  floorHeight?: number;
  onContentHeightChange?: (height: number) => void;
}> = ({
  value,
  onChange,
  onBlur,
  hasLeftIcon,
  columnKey,
  floorHeight,
  onContentHeightChange,
}) => {
  const [localValue, setLocalValue] = useState(() =>
    value === null || value === undefined ? "" : String(value),
  );
  const [isExpanded, setIsExpanded] = useState(false);

  const closingRef = useRef(false);
  const expandingRef = useRef(false);
  const triggerRef = useRef<HTMLDivElement>(null);
  const expandedHeightRef = useRef<number | null>(null);

  const {
    textareaRef,
    mirrorRef,
    hasScrolledContent,
    handleScroll,
    markTyped,
    markCancelled,
  } = useStringCellHeight({
    localValue,
    floorHeight,
    onHeightChange: onContentHeightChange,
  });

  const commitValue = useCallback(() => {
    if (localValue === "") {
      onChange(null);
    } else {
      onChange(localValue);
    }
  }, [localValue, onChange]);

  const handleCommit = () => {
    if (closingRef.current || expandingRef.current) return;
    commitValue();
    onBlur();
  };

  const handleTextChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    markTyped();
    setLocalValue(e.target.value);
  };

  const handleExpandClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    expandingRef.current = true;
    const wrapper = textareaRef.current?.parentElement;
    expandedHeightRef.current = wrapper ? wrapper.offsetHeight : null;
    setIsExpanded(true);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    switch (e.key) {
      case "Enter":
        if (e.shiftKey) {
          e.preventDefault();
          closingRef.current = true;
          commitValue();
          return;
        }
        if (e.metaKey || e.ctrlKey) {
          e.preventDefault();
          closingRef.current = true;
          commitValue();
          onBlur();
          break;
        }
        e.stopPropagation();
        return;

      case "Escape":
        e.preventDefault();
        closingRef.current = true;
        markCancelled();
        onBlur();
        break;

      case "ArrowUp":
      case "ArrowDown": {
        const ta = textareaRef.current;
        if (!ta || !localValue.includes("\n")) return;

        const pos = ta.selectionStart;
        if (e.key === "ArrowUp") {
          if (!ta.value.substring(0, pos).includes("\n")) return;
        } else {
          if (!ta.value.substring(pos).includes("\n")) return;
        }
        e.stopPropagation();
        return;
      }

      case "ArrowLeft":
      case "ArrowRight":
        e.stopPropagation();
        return;

      case "Tab":
        commitValue();
        return;
    }
    e.stopPropagation();
  };

  const dataLoc = `text-editor-${columnKey}`;

  useOnClickOutside(triggerRef, (e) => {
    if (!isExpanded) return;
    if ((e.target as Element).closest?.(`[data-loc="${dataLoc}"]`)) return;
    commitValue();
    onBlur();
  });

  const handleExpandedClose = () => {
    commitValue();
    onBlur();
  };

  const handleExpandedKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Tab") {
      commitValue();
      return;
    }
    e.stopPropagation();
    if (e.key === "Escape") {
      e.preventDefault();
      handleExpandedClose();
    }
  };

  const handleCopy = () => {
    copyTextToClipboard(localValue);
  };

  const textareaRefCallback = useCallback(
    (node: HTMLTextAreaElement | null) => {
      textareaRef.current = node;
      if (node) {
        node.focus();
        node.selectionStart = node.value.length;
        node.selectionEnd = node.value.length;
      }
    },
    [],
  );

  if (isExpanded) {
    return (
      <div
        ref={triggerRef}
        className="w-full min-w-0"
        style={{ height: expandedHeightRef.current ?? undefined }}
        onKeyDown={handleExpandedKeyDown}
      >
        <FloatingWindow
          button={
            // Anchor box for the window; it clips the natural-height text
            // mirror. Explicit pixels: FloatingWindow renders the trigger
            // in an inline span, where h-full can't resolve.
            <div
              className="w-full overflow-hidden"
              style={{ height: expandedHeightRef.current ?? undefined }}
            >
              <div
                ref={mirrorRef}
                className={twJoin(
                  "w-full py-1.5 pr-2",
                  hasLeftIcon ? "pl-6" : "pl-1.5",
                )}
              >
                <div className="whitespace-pre-wrap break-words text-gray-500">
                  {localValue || "\u00A0"}
                </div>
              </div>
            </div>
          }
          dataLoc={dataLoc}
          draggable={true}
          height="h-48"
          isOpen={true}
          isTrackingReference={true}
          maximizable={true}
          offset={[0, 8]}
          pinnable={false}
          placement="bottom start"
          resizeable={true}
          title={columnKey}
          width="w-80"
          onClose={handleExpandedClose}
          onCopy={handleCopy}
        >
          <textarea
            ref={(node) => node?.focus()}
            className="decideScrollbar h-full w-full resize-none bg-transparent p-2 font-inter-normal-12px outline-none"
            value={localValue}
            onChange={handleTextChange}
            onKeyDown={(e) => {
              if (e.key === "Enter" && (e.shiftKey || e.metaKey || e.ctrlKey)) {
                e.preventDefault();
                handleExpandedClose();
              }
            }}
          />
        </FloatingWindow>
      </div>
    );
  }

  return (
    <>
      <div className="relative h-full w-full overflow-hidden">
        <textarea
          ref={textareaRefCallback}
          className={twJoin(
            // Fills the cell at its current height and scrolls inside it.
            "decideScrollbarThin block h-full w-full resize-none bg-transparent py-1.5 pr-2 font-inter-normal-12px outline-none",
            hasLeftIcon ? "pl-6" : "pl-1.5",
          )}
          rows={1}
          style={{ minHeight: MIN_STRING_CELL_HEIGHT }}
          value={localValue}
          onBlur={handleCommit}
          onChange={handleTextChange}
          onKeyDown={handleKeyDown}
          onScroll={handleScroll}
        />

        {hasScrolledContent && (
          <div className="pointer-events-none absolute inset-x-0 top-0 h-4 bg-gradient-to-b from-indigo-50 to-transparent" />
        )}
      </div>

      {/* Absolute so cell content doesn't realign when the editor closes.
          flex removes the baseline gap under the button. */}
      <div className="absolute right-1 bottom-1 z-20 flex rounded border border-gray-200 bg-white shadow-sm hover:bg-gray-50">
        {/* Label-only, and it opens right over the type selector above it, so
            it must close with the pointer instead of stacking with it. */}
        <Tooltip
          isInteractive={false}
          placement="top"
          title="Expand editor"
          asChild
        >
          <IconButton
            dataLoc="text-cell-expand"
            icon={faExpand}
            size="2xs"
            onClick={handleExpandClick}
            onMouseDown={(e) => e.preventDefault()}
          />
        </Tooltip>
      </div>
    </>
  );
};
